import { reactive, watch } from 'vue';
import { useRoadmapsStore } from '@/stores/roadmaps';
import { formatRange } from '@/lib/schema';

const state = reactive({ events: [], kinds: new Set(), started: false });
let previous = null;
let counter = 0;

function snapshot(roadmaps) {
  const out = new Map();
  for (const rm of roadmaps) {
    const phases = new Map(rm.phases.map((p) => [p.id, p.name]));
    const projects = new Map();
    for (const p of rm.projects) {
      projects.set(p.id, {
        name: p.name,
        group: p.group || '',
        status: p.status || '',
        people: p.memberIds.length,
        blocks: new Map(p.blocks.map((b) => [b.id, { ...b, phase: phases.get(b.phaseId) || 'No phase' }]))
      });
    }
    out.set(rm.id, { name: rm.name, half: rm.settings.halfSprints, projects });
  }
  return out;
}

function diff(before, after) {
  const found = [];
  for (const [id, rm] of after) {
    const old = before.get(id);
    if (!old) continue;
    if (old.half !== rm.half) found.push({ kind: rm.half ? 'half' : 'whole', text: `Turned half sprints ${rm.half ? 'on' : 'off'} for ${rm.name}` });
    for (const [pid, p] of rm.projects) {
      const op = old.projects.get(pid);
      if (!op) {
        found.push({ kind: 'project', text: `Added the project ${p.name}` });
        continue;
      }
      if (op.name !== p.name) found.push({ kind: 'project', text: `Renamed ${op.name} to ${p.name}` });
      if (op.group !== p.group) found.push({ kind: 'group', text: `Moved ${p.name} to ${p.group || 'no group'}` });
      if (op.status !== p.status) found.push({ kind: 'status', text: `Marked ${p.name} as ${p.status || 'no status'}` });
      if (op.people !== p.people) found.push({ kind: 'people', text: `Changed who works on ${p.name}` });
      for (const [bid, b] of p.blocks) {
        const ob = op.blocks.get(bid);
        if (!ob) {
          found.push({ kind: 'create', text: `Added ${b.phase} to ${p.name}, ${formatRange(b.start, b.span)}` });
          continue;
        }
        if (ob.phaseId !== b.phaseId) found.push({ kind: 'phase', text: `Changed a ${p.name} block to ${b.phase}` });
        if (ob.span !== b.span) found.push({ kind: 'resize', text: `Resized ${b.phase} on ${p.name} to ${formatRange(b.start, b.span)}` });
        else if (ob.start !== b.start) found.push({ kind: 'move', text: `Moved ${b.phase} on ${p.name} to ${formatRange(b.start, b.span)}` });
      }
      for (const [bid, ob] of op.blocks) {
        if (!p.blocks.has(bid)) found.push({ kind: 'remove', text: `Removed ${ob.phase} from ${p.name}` });
      }
    }
    for (const [pid, op] of old.projects) {
      if (!rm.projects.has(pid)) found.push({ kind: 'project', text: `Deleted the project ${op.name}` });
    }
  }
  return found;
}

export function resetDemoEvents(roadmaps) {
  state.events.splice(0);
  state.kinds = new Set();
  previous = snapshot(roadmaps);
}

export function useDemoEvents() {
  const store = useRoadmapsStore();
  if (!state.started) {
    state.started = true;
    watch(
      () => store.revision,
      () => {
        const next = snapshot(store.roadmaps);
        if (previous) {
          for (const e of diff(previous, next)) {
            state.kinds.add(e.kind);
            state.events.unshift({ ...e, id: ++counter, time: new Date() });
          }
          if (state.events.length > 40) state.events.splice(40);
        }
        previous = next;
      }
    );
    watch(
      () => store.ready,
      (ready) => {
        if (ready) previous = snapshot(store.roadmaps);
      },
      { immediate: true }
    );
  }
  return state;
}
